import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import type { ApiCart } from 'shared';
import { API_BASE_URL } from 'shared';
import { AuthService } from './auth.service';

export interface CartItem {
  id: string;
  productId: string;
  productVariantId?: string;
  slug: string;
  name: string;
  description: string;
  image: string;
  unitPrice: number;
  qty: number;
}

const STORAGE_KEY = 'kota_cart';

@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly http = inject(HttpClient);
  private readonly base = inject(API_BASE_URL);
  private readonly auth = inject(AuthService);

  private readonly _items = signal<CartItem[]>(this.loadLocal());
  private readonly _syncing = signal(false);
  private readonly _removingIds = signal<Set<string>>(new Set());

  readonly items = this._items.asReadonly();
  readonly syncing = this._syncing.asReadonly();
  readonly itemCount = computed(() => this._items().reduce((s, i) => s + i.qty, 0));
  readonly subtotal = computed(() => this._items().reduce((s, i) => s + i.unitPrice * i.qty, 0));
  readonly shippingFree = computed(() => this.subtotal() >= 10000);
  readonly total = computed(() => this.subtotal());

  constructor() {
    if (this.auth.isLoggedIn()) {
      this.reloadFromApi();
    }

    effect(() => {
      if (!this.auth.isLoggedIn()) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this._items()));
      }
    });
  }

  /** Llamado por AuthService después del login para migrar carrito de invitado al API. */
  onLogin(): void {
    const guestItems = this.loadLocal();
    if (!guestItems.length) {
      this.reloadFromApi();
      return;
    }
    const pushNext = (index: number): void => {
      if (index >= guestItems.length) {
        localStorage.removeItem(STORAGE_KEY);
        this.reloadFromApi();
        return;
      }
      const item = guestItems[index];
      this.http
        .post(
          `${this.base}/cart/items`,
          { product_id: Number(item.productId), quantity: item.qty },
          { withCredentials: true }
        )
        .subscribe({ complete: () => pushNext(index + 1), error: () => pushNext(index + 1) });
    };
    pushNext(0);
  }

  add(item: Omit<CartItem, 'id'>): void {
    this.addLocal(item);
    if (!this.auth.isLoggedIn()) return;
    this.http
      .post(
        `${this.base}/cart/items`,
        {
          product_id: Number(item.productId),
          product_variant_id: item.productVariantId ? Number(item.productVariantId) : null,
          quantity: item.qty
        },
        { withCredentials: true }
      )
      .subscribe({
        error: (err) => console.error('Error agregando al carrito', err)
      });
  }

  inc(id: string): void {
    if (!this.auth.isLoggedIn() || !this.isBackendId(id)) {
      this._items.update((list) => list.map((i) => (i.id === id ? { ...i, qty: i.qty + 1 } : i)));
      return;
    }
    const item = this._items().find((i) => i.id === id);
    if (item) this.patchQty(id, item.qty + 1);
  }

  dec(id: string): void {
    if (!this.auth.isLoggedIn() || !this.isBackendId(id)) {
      this._items.update((list) =>
        list.map((i) => (i.id === id ? { ...i, qty: Math.max(1, i.qty - 1) } : i))
      );
      return;
    }
    const item = this._items().find((i) => i.id === id);
    if (item && item.qty > 1) this.patchQty(id, item.qty - 1);
  }

  remove(id: string): void {
    if (!this.auth.isLoggedIn() || !this.isBackendId(id)) {
      this._items.update((list) => list.filter((i) => i.id !== id));
      return;
    }
    this.setRemoving(id, true);
    this.http
      .delete(`${this.base}/cart/items/${id}`, { withCredentials: true })
      .subscribe({
        next: () => {
          this._items.update((list) => list.filter((i) => i.id !== id));
          this.setRemoving(id, false);
        },
        error: (err) => {
          console.error('Error eliminando ítem del carrito', err);
          this.setRemoving(id, false);
        }
      });
  }

  /** Indica si un ítem está en proceso de eliminación en el API. */
  isRemoving(id: string): boolean {
    return this._removingIds().has(id);
  }

  private setRemoving(id: string, removing: boolean): void {
    this._removingIds.update((ids) => {
      const next = new Set(ids);
      if (removing) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  private isBackendId(id: string): boolean {
    return /^\d+$/.test(id);
  }

  clear(): void {
    this._items.set([]);
    localStorage.removeItem(STORAGE_KEY);
    if (this.auth.isLoggedIn()) {
      this.http.delete(`${this.base}/cart/items`, { withCredentials: true }).subscribe();
    }
  }

  formatMoney(n: number): string {
    return '$' + n.toLocaleString('es-MX');
  }

  private reloadFromApi(): void {
    this._syncing.set(true);
    this.http
      .get<ApiCart>(`${this.base}/cart`, { withCredentials: true })
      .subscribe({
        next: (cart) => {
          this._items.set(cart.cart_items.map((ci) => ({
            id: String(ci.id),
            productId: String(ci.product_id),
            productVariantId: ci.product_variant_id ? String(ci.product_variant_id) : undefined,
            slug: ci.product?.sku ?? String(ci.product_id),
            name: ci.product?.product_name ?? '',
            description: ci.product?.description ?? '',
            image: [...(ci.product?.product_images ?? [])].sort((a, b) => {
              if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
              return a.sort_order - b.sort_order;
            })[0]?.image_url ?? '',
            unitPrice: parseFloat(ci.product?.price ?? '0') || 0,
            qty: ci.quantity
          })));
          this._syncing.set(false);
        },
        error: (err) => {
          console.error('Error cargando carrito', err);
          this._syncing.set(false);
        }
      });
  }

  private patchQty(id: string, quantity: number): void {
    this.http
      .patch(`${this.base}/cart/items/${id}`, { quantity }, { withCredentials: true })
      .subscribe({
        next: () => this._items.update((list) =>
          list.map((i) => (i.id === id ? { ...i, qty: quantity } : i))
        ),
        error: (err) => console.error('Error actualizando cantidad', err)
      });
  }

  private addLocal(item: Omit<CartItem, 'id'>): void {
    this._items.update((list) => {
      const existing = list.find(
        (i) => i.productId === item.productId && i.description === item.description
      );
      if (existing) {
        return list.map((i) => (i === existing ? { ...i, qty: i.qty + item.qty } : i));
      }
      return [...list, { ...item, id: crypto.randomUUID() }];
    });
  }

  private loadLocal(): CartItem[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
      return [];
    }
  }
}
